import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { KanbanColumn } from '@/components/tasks/kanban-column';
import { TaskCard } from '@/components/tasks/task-card';
import { useMoveTask } from '@/hooks/use-tasks';
import { useBoardStore } from '@/stores/board-store';
import { toast } from '@/components/ui/toaster';
import { registerView, type ViewProps } from '@/components/workspace/ViewRegistry';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { Task } from '@/types';

function BoardView({ projectId, listId, tasks, statuses, isLoading, isClient: _isClient }: ViewProps) {
  const moveTask = useMoveTask();
  const queryClient = useQueryClient();
  const { setDraggedTaskId } = useBoardStore();
  const { data: capabilities } = useMyCapabilities();
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [showClosedTasks, setShowClosedTasks] = useState(false);

  const canDrag = Boolean(capabilities?.['task.status.change']);
  const canCreateTask = Boolean(capabilities?.['task.create']);

  // Filter out closed/done tasks by default
  const filteredTasks = useMemo(() => {
    if (showClosedTasks) return tasks;
    const doneStatusIds = new Set(
      statuses.filter((s) => s.isDoneState).map((s) => s.id)
    );
    return tasks.filter((t) => !doneStatusIds.has(t.statusId));
  }, [tasks, statuses, showClosedTasks]);

  // Filter out done statuses when closed tasks are hidden
  const visibleStatuses = useMemo(() => {
    if (showClosedTasks) return statuses;
    return statuses.filter((s) => !s.isDoneState);
  }, [statuses, showClosedTasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Pre-compute tasks grouped by status to avoid repeated filtering
  const tasksByStatusId = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of filteredTasks) {
      const existing = map.get(task.statusId) ?? [];
      existing.push(task);
      map.set(task.statusId, existing);
    }
    // Sort each status's tasks by position
    for (const tasks of map.values()) {
      tasks.sort((a, b) => a.position - b.position);
    }
    return map;
  }, [filteredTasks]);

  const getTasksByStatus = useCallback(
    (statusId: string) => {
      return tasksByStatusId.get(statusId) ?? [];
    },
    [tasksByStatusId]
  );

  const sortedStatuses = useMemo(
    () => [...visibleStatuses].sort((a, b) => a.position - b.position),
    [visibleStatuses]
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (!canDrag) return;

    const { active } = event;
    const task = tasks.find((t) => t.id === active.id);
    if (task) {
      setActiveTask(task);
      setDraggedTaskId(task.id);
    }
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Visual feedback is handled by column isOver state
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    setDraggedTaskId(null);

    if (!canDrag || !over) return;

    const taskId = active.id as string;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    let targetStatusId: string;
    let newPosition: number | undefined;

    if (over.id.toString().startsWith('column-')) {
      targetStatusId = over.id.toString().replace('column-', '');
      newPosition = getTasksByStatus(targetStatusId).length;
    } else {
      const overTask = tasks.find((t) => t.id === over.id);
      if (!overTask) return;

      targetStatusId = overTask.statusId;
      const statusTasks = getTasksByStatus(targetStatusId);
      const overIndex = statusTasks.findIndex((t) => t.id === overTask.id);
      newPosition = overIndex;
    }

    if (task.statusId === targetStatusId && task.position === newPosition) {
      return;
    }

    // Optimistic update — scope-aware list caches use ['tasks', projectId, listId, visibilityScope]
    const listTasksQueryFilter = { queryKey: ['tasks', projectId, listId] as const };
    const snapshots = queryClient.getQueriesData<Task[]>(listTasksQueryFilter);

    queryClient.setQueriesData<Task[]>(listTasksQueryFilter, (old) => {
      if (!old) return old;
      return old.map((t) =>
        t.id === taskId
          ? { ...t, statusId: targetStatusId, position: newPosition ?? t.position }
          : t
      );
    });

    moveTask.mutate(
      {
        id: taskId,
        projectId,
        listId,
        statusId: targetStatusId,
        position: newPosition,
      },
      {
        onError: () => {
          snapshots.forEach(([key, data]) => {
            queryClient.setQueryData(key, data);
          });
          toast({
            title: 'Failed to move task',
            description: 'The task could not be moved. Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const boardToolbar = (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-card/50">
      <div className="flex items-center gap-2">
        <Switch
          id="board-show-closed-tasks"
          checked={showClosedTasks}
          onCheckedChange={setShowClosedTasks}
          className="scale-90"
        />
        <Label htmlFor="board-show-closed-tasks" className="text-xs text-muted-foreground cursor-pointer">
          Show closed tasks
        </Label>
      </div>
    </div>
  );

  const boardContent = (
    <div className="flex h-full gap-4 overflow-x-auto p-4">
      {sortedStatuses.map((status) => (
        <KanbanColumn
          key={status.id}
          status={status}
          tasks={getTasksByStatus(status.id)}
          canCreateTask={canCreateTask}
          canDragTasks={canDrag}
        />
      ))}
    </div>
  );

  if (!canDrag) {
    return (
      <div className="flex h-full flex-col">
        {boardToolbar}
        <div className="flex-1 overflow-hidden">{boardContent}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {boardToolbar}
      <div className="flex-1 overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {boardContent}

          <DragOverlay>
            {activeTask && <TaskCard task={activeTask} isDragging />}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}

registerView({
  id: 'board',
  label: 'Board',
  icon: 'LayoutGrid',
  component: BoardView,
});

export { BoardView };
